import { customElement, scss } from '../../core/ids-decorators';
import { attributes, htmlAttributes } from '../../core/ids-attributes';
import { isValidBooleanValue, stringToBool } from '../../utils/ids-string-utils/ids-string-utils';

import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import debounce from '../../utils/ids-debounce-utils/ids-debounce-utils';
import IdsColorVariantMixin from '../../mixins/ids-color-variant-mixin/ids-color-variant-mixin';
import IdsElement from '../../core/ids-element';

import styles from './ids-container.scss';

const Base = IdsLocaleMixin(
  IdsColorVariantMixin(
    IdsEventsMixin(
      IdsElement
    )
  )
);

/**
 * IDS Container Component
 * @type {IdsContainer}
 * @inherits IdsElement
 * @mixes IdsEventsMixin
 * @mixes IdsLocaleMixin
 * @part container - the entire container element
 */
@customElement('ids-container')
@scss(styles)
export default class IdsContainer extends Base {
  /**
   * ResizeObserver instance for tracking container size changes
   * @private
   */
  #resizeObserver?: ResizeObserver;

  constructor() {
    super();
  }

  /**
   * Invoked each time the custom element is appended into a document-connected element.
   */
  connectedCallback() {
    super.connectedCallback();
    if (this.reset) {
      this.#addReset();
    }

    // Remove hidden for FOUC
    this.onEvent('load.container', window, () => {
      this.removeAttribute('hidden');
      this.offEvent('load.container', window);
    });

    // In some cases the page may be loaded
    if (document.readyState === 'complete') {
      this.removeAttribute('hidden');
    }

    this.container?.style.setProperty('padding', `${this.padding}px`);
    if (this.backgroundColor) {
      this.backgroundColor = this.getAttribute('background-color') || '';
    }

    requestAnimationFrame(() => {
      this.#initEvents();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes in an array
   */
  static get attributes() {
    return [
      ...super.attributes,
      attributes.BACKGROUND_COLOR,
      attributes.EVENT_DEBOUNCE,
      attributes.PADDING,
      attributes.RESET,
      attributes.SCROLLABLE
    ];
  }

  /**
   * Create the Template for the contents
   * @returns {string} The template
   */
  template(): string {
    const scrollableAttrs = this.scrollable === 'true'
      ? ' tabindex="0" role="group"'
      : '';
    return `<div class="ids-container" part="container"${scrollableAttrs}><slot></slot></div>`;
  }

  /**
   * Inherited from `IdsColorVariantMixin`
   * @returns {Array<string>} List of available color variants for this component
   */
  colorVariants = ['alternate'];

  /**
   * If set to number the container will have padding added (in pixels)
   * @param {string} value sets the padding to the container
   */
  set padding(value: string) {
    if (this.container) this.container.style.padding = `${value}px`;
    this.setAttribute(attributes.PADDING, value.toString());
  }

  get padding(): any {
    return this.getAttribute(attributes.PADDING);
  }

  set eventDebounce(value: boolean | string) {
    if (!isValidBooleanValue(value)) return;

    const toDebounce = stringToBool(value);
    this.toggleAttribute(attributes.TREND_LABEL_NO_ICON, toDebounce);

    this.#initEvents();
  }

  get eventDebounce(): boolean {
    return this.hasAttribute(attributes.EVENT_DEBOUNCE);
  }

  /**
   * If set to true the container is scrollable
   * @param {boolean | string} value true of false depending if the tag is scrollable
   */
  set scrollable(value: boolean | string) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.SCROLLABLE, 'true');
      this.container?.setAttribute(attributes.SCROLLABLE, 'true');
      this.container?.setAttribute(attributes.TABINDEX, '0');
      this.container?.setAttribute(htmlAttributes.ROLE, 'group');
      return;
    }

    this.setAttribute(attributes.SCROLLABLE, 'false');
    this.container?.setAttribute(attributes.SCROLLABLE, 'false');
    this.container?.removeAttribute(attributes.TABINDEX);
    this.container?.removeAttribute(htmlAttributes.ROLE);
  }

  get scrollable(): boolean | string { return this.getAttribute(attributes.SCROLLABLE) || 'true'; }

  /**
   * Initializes or reinitializes scroll and resize event listeners.
   * Cleans up existing observers and event listeners before reattaching with current debounce settings.
   * @private
   */
  #initEvents() {
    this.#resizeObserver?.disconnect?.();

    this.offEvent('scroll', this.container);

    const toDebounce = this.eventDebounce;

    this.#attachResizeEvent(toDebounce);
    this.#attachScrollEvent(toDebounce);
  }

  /**
   * Attaches scroll event listener to container and re-dispatches to host
   * @param { boolean } toDebounce whether to debounce the event
   * @private
   */
  #attachScrollEvent(toDebounce: boolean) {
    if (toDebounce) {
      this.onEvent('scroll', this.container, debounce((e: Event) => {
        this.triggerEvent('scroll', this, {
          bubbles: true,
          detail: {
            event: e
          }
        });
      }, 50));

      return;
    }

    this.onEvent('scroll', this.container, (e: Event) => {
      this.triggerEvent('scroll', this, {
        bubbles: true,
        detail: {
          event: e
        }
      });
    });
  }

  /**
   * Attaches ResizeObserver to container and dispatches resize events to host
   * @param { boolean } toDebounce whether to debounce the event
   * @private
   */
  #attachResizeEvent(toDebounce: boolean) {
    if (toDebounce) {
      this.#resizeObserver = new ResizeObserver(debounce((entries: ResizeObserverEntry[]) => {
        for (const entry of entries) {
          this.triggerEvent('resize', this, {
            bubbles: true,
            detail: {
              width: entry.contentRect.width,
              height: entry.contentRect.height,
              event: entry
            }
          });
        }
      }, 50));
    } else {
      this.#resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
        for (const entry of entries) {
          this.triggerEvent('resize', this, {
            bubbles: true,
            detail: {
              width: entry.contentRect.width,
              height: entry.contentRect.height,
              event: entry
            }
          });
        }
      });
    }

    if (this.container) {
      this.#resizeObserver.observe(this.container);
    }
  }

  /**
   * Add the reset to the body
   * @private
   */
  #addReset() {
    document.querySelector('body')?.style.setProperty('margin', '0');
  }

  /**
   * If set to true body element will get a "css reset"
   * @param {boolean | string} value true of false
   */
  set reset(value: boolean | string) {
    if (stringToBool(value)) {
      this.#addReset();
      return;
    }
    this.removeAttribute(attributes.RESET);
    document.querySelector('body')?.style.setProperty('margin', '');
  }

  get reset(): boolean | string { return this.getAttribute(attributes.RESET) || 'true'; }

  /**
   * Pass in a css variable name to set the background color
   * @param {string} value css variable to use
   */
  set backgroundColor(value: string) {
    if (value) {
      this.container?.style.setProperty('background-color', `var(${value})`);
      return;
    }
    this.container?.style.setProperty('background-color', '');
  }

  get backgroundColor(): string { return this.getAttribute(attributes.BACKGROUND_COLOR) || 'true'; }
}
